"use client";

import { createBrowserClient } from "@supabase/ssr";
import type { SupabaseClient } from "@supabase/supabase-js";

/** Один экземпляр на вкладку: стабильная сессия и Realtime (несколько createClient ломали подписки). */
let browserClient: SupabaseClient | undefined;
const authLockTails = new Map<string, Promise<void>>();

/**
 * Stable in-memory lock for auth operations.
 * Prevents noisy "lock stolen/broken" errors from navigator.locks in dev.
 */
async function withAuthLock<T>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<T>
): Promise<T> {
  const prevTail = authLockTails.get(name) ?? Promise.resolve();
  let release!: () => void;
  const currentTail = new Promise<void>((resolve) => {
    release = resolve;
  });
  const nextTail = prevTail.then(() => currentTail);
  authLockTails.set(name, nextTail);

  await prevTail;
  try {
    return await fn();
  } finally {
    release();
    if (authLockTails.get(name) === nextTail) {
      authLockTails.delete(name);
    }
  }
}

export function createClient(): SupabaseClient {
  if (browserClient) return browserClient;
  browserClient = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        lock: withAuthLock,
      },
    }
  );
  return browserClient;
}
